import { useState } from "react";
import {
  uploadimgticket,
  getimgticket,
  updateSupport,
} from "../../../../../services/supports/supports";
import { showAlert } from "../../../../../main_components/alerts/alert_response";

export interface AttachedFile {
  file: File;
  renamedName: string;
}

interface TicketData {
  ticket: string;
  images: string[];
  [key: string]: any;
}

interface UseTicketDocumentsProps {
  ticketData: TicketData;
  notificationSystemRef: any;
  onTicketUpdate?: (updatedTicket: TicketData) => void;
  getCurrentTimestamp: () => string;
  formatFileNameByType: (file: File) => string | null;
}

export const useTicketDocuments = ({
  ticketData,
  notificationSystemRef,
  onTicketUpdate,
  getCurrentTimestamp,
  formatFileNameByType,
}: UseTicketDocumentsProps) => {
  const [ticketDocuments, setTicketDocuments] = useState<any[]>([]);
  const [loadingTicketDocuments, setLoadingTicketDocuments] = useState(true);
  const [pendingTicketFiles, setPendingTicketFiles] = useState<AttachedFile[]>(
    []
  );
  const [uploadingFiles, setUploadingFiles] = useState(false);

  const fileToBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const result = reader.result;
        if (typeof result === "string") {
          resolve(result.split(",")[1] || "");
        } else {
          reject(new Error("Error al convertir archivo"));
        }
      };
      reader.onerror = () => reject(new Error("Error al leer archivo"));
      reader.readAsDataURL(file);
    });
  };

  const loadTicketDocuments = async () => {
    setLoadingTicketDocuments(true);
    try {
      const downloads = await Promise.all(
        (ticketData.images || []).map(async (fileName) => {
          try {
            const res = await getimgticket(ticketData.ticket, fileName);
            return { file_name: res.data.file_name, file: res.data.file };
          } catch {
            return null;
          }
        })
      );
      setTicketDocuments(downloads.filter(Boolean));
    } catch (err) {
      console.error("Error al cargar archivos:", err);
      setTicketDocuments([]);
    } finally {
      setLoadingTicketDocuments(false);
    }
  };

  const handleTicketFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files) return;
    const newFiles = Array.from(e.target.files);
    const validFiles: AttachedFile[] = [];

    newFiles.forEach((file) => {
      const maxSizeMB = 4.9;
      if (file.size / (1024 * 1024) > maxSizeMB) {
        showAlert({
          msj: `Archivo demasiado grande: ${file.name}`,
          status: false,
          notificationSystemRef,
        });
        return;
      }

      const renamed = formatFileNameByType(file);
      if (!renamed) {
        showAlert({
          msj: `Formato no permitido: ${file.name}`,
          status: false,
          notificationSystemRef,
        });
        return;
      }

      validFiles.push({ file, renamedName: renamed });
    });

    setPendingTicketFiles((prev) => [...prev, ...validFiles]);
  };

  const handleUploadPendingTicketFiles = async () => {
    setUploadingFiles(true);
    const uploaded: any[] = [];

    for (const fileObj of pendingTicketFiles) {
      try {
        const base64 = await fileToBase64(fileObj.file);
        const res = await uploadimgticket(ticketData.ticket, {
          file_name: fileObj.renamedName,
          file_b64: base64,
        });
        uploaded.push({ file_name: res.data.file_name, file: base64 });
      } catch (err) {
        console.error("Error subiendo archivo:", err);
      }
    }

    setPendingTicketFiles([]);

    const fileNames = uploaded.map((f) => f.file_name);
    if (fileNames.length > 0) {
      try {

        await updateSupport(ticketData.ticket, { images: fileNames });
        const timestamp = getCurrentTimestamp();
        setTicketDocuments((prev) => [...prev, ...uploaded]);

        onTicketUpdate?.({
          ...ticketData,
          images: [...(ticketData.images || []), ...fileNames],
          last_update: timestamp,
        });
        showAlert({
          msj: "Archivos subidos correctamente",
          status: true,
          notificationSystemRef,
        });
      } catch (err: any) {
        showAlert({
          msj: err.response?.data?.errorMessage || "Error al actualizar ticket",
          status: false,
          notificationSystemRef,
        });
      }
    }

    setUploadingFiles(false);
  };

  const removePendingFile = (index: number) => {
    setPendingTicketFiles((prev) => prev.filter((_, i) => i !== index));
  };

  return {
    ticketDocuments,
    loadingTicketDocuments,
    pendingTicketFiles,
    uploadingFiles,
    setPendingTicketFiles,
    loadTicketDocuments,
    handleTicketFileUpload,
    handleUploadPendingTicketFiles,
    removePendingFile,
    setLoadingTicketDocuments,
  };
};
